import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import InfoTooltip from 'components/InfoTooltip';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { useRef } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { MatterForm } from 'types/matters';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';

type Props = {
    control: Control<MatterForm>;
    setValue: UseFormSetValue<MatterForm>;
    getValues: UseFormGetValues<MatterForm>;
    trigger: UseFormTrigger<MatterForm>;
    classifiers: Record<string, ClassifierData>;
};

export function OfferValueInput({ control, setValue, getValues, trigger, classifiers }: Props) {
    // Copying Low into High is a one-shot convenience on the first blur of Low: afterwards the two
    // amounts move independently, so a later edit of Low must leave High untouched.
    const hasCopiedLowIntoHighRef = useRef(false);
    // On the record page the three controls are one cell on a row of its own, where the read view has the value's
    // line, under a label that runs over them rather than wrap in the first one's width.
    const inSection = useInRecordSection();

    function copyLowIntoHighOnce(low?: string) {
        if (!low) {
            return;
        }

        const high = getValues('offerValueHigh');
        if (!hasCopiedLowIntoHighRef.current && (high === null || high === '')) {
            setValue('offerValueHigh', low, { shouldDirty: true });
        }

        hasCopiedLowIntoHighRef.current = true;
    }

    const fields = (
        <>
            <SelectInput
                control={control}
                setValue={setValue}
                wrapperClassName={inSection ? 'min-w-0 flex-1 [&_label]:whitespace-nowrap' : undefined}
                name="offerValueType"
                label="matters.offer-value"
                tooltip={<InfoTooltip text="matters.offer-value-tooltip" />}
                options={
                    (getValidOptionsFromClassifier(classifiers['OFFER_VALUE_TYPE']) as {
                        label: string;
                        value: string;
                    }[]) ?? []
                }
                getOptionLabel="label"
                getOptionIdentifier="value"
                clearable={false}
            />
            <div className={inSection ? 'flex min-w-0 flex-[2] gap-6' : 'flex flex-col gap-2 sm:flex-row'}>
                <div className="flex-1">
                    <DecimalInput
                        control={control}
                        name="offerValueLow"
                        placeholder="matters.offer-value-low"
                        onBlur={copyLowIntoHighOnce}
                        // The range rule is registered on High, so Low has to ask for it to be re-evaluated.
                        onChange={() => void trigger('offerValueHigh')}
                    />
                </div>
                <div className="flex-1">
                    <DecimalInput
                        control={control}
                        name="offerValueHigh"
                        placeholder="matters.offer-value-high"
                        validate={validateHighAgainstLow}
                        // Touching High at all counts as taking control of it, so the one-shot copy must not fire later.
                        onChange={() => {
                            hasCopiedLowIntoHighRef.current = true;
                        }}
                    />
                </div>
            </div>
        </>
    );
    return inSection ? <div className="form-field col-span-full flex items-end gap-6">{fields}</div> : fields;
}

function validateHighAgainstLow(high: unknown, form: MatterForm): true | string {
    const lowAmount = toAmount(form.offerValueLow);
    const highAmount = toAmount(high);

    if (lowAmount === null || highAmount === null) {
        return true;
    }

    return highAmount >= lowAmount ? true : t('matters.offer-value-high-error');
}

function toAmount(value: unknown): number | null {
    // The API sends the amounts as numbers, while the inputs keep them as strings.
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string' || value.trim() === '') {
        return null;
    }

    const amount = Number(value.replace(',', '.'));
    return Number.isFinite(amount) ? amount : null;
}
